import React, { useEffect, useState, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Dialog, Transition, Popover } from "@headlessui/react";
import clsx from "clsx";
import Layout from "../../layouts";
import Dropzone from "../../components/Dropzone";
import FieldError from "../../components/FieldError";
import Breadcrumb from "../../components/Breadcrumb";
import { useTeacherStore } from "../../store";
import { validateTeacherNewForm } from "../../services/validators";
import { AutoTabProvider } from "react-auto-tab";

export default function NewForm() {
  const formRef = useRef();
  const navigate = useNavigate();
  const [messages, setMessages] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [showSchoolProof, setShowSchoolProof] = useState(false);
  const store = useTeacherStore();

  useEffect(() => {
    if (!store.form.agree) {
      navigate("/teacher");
      return;
    }
  }, [navigate, store.form.agree]);

  useEffect(() => {
    if (!store.form.schoolProofAck) {
      setShowSchoolProof(true);
    }
  }, [store.form.schoolProofAck]);

  useEffect(() => {
    window.addEventListener("beforeunload", alertBeforeLeaving);
    window.scrollTo(0, 0);
    return () => {
      window.removeEventListener("beforeunload", alertBeforeLeaving);
    };
  }, []);

  const alertBeforeLeaving = (e) => {
    e.preventDefault();
    e.returnValue = "";
  };

  const readFileASync = (file) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  };

  const onPhotoRemoved = () => {
    store.updateForm("photo", null);
  };

  const onPhotoDropAccepted = async (file) => {
    const contentBuffer = await readFileASync(file);
    store.updateForm("photo", contentBuffer);
  };

  const onPhotoDropRejected = (file) => {
    if (file.errors) {
      file.errors.forEach((error) => {
        alert(error.message);
        return;
      });
    }
  };

  const onSchoolProofSelected = async (file) => {
    const contentBuffer = await readFileASync(file);
    store.updateForm('schoolProof', contentBuffer);
  };

  const onSchoolProofRemoved = () => {
    store.updateForm('schoolProof', null);
  };

  const onSchoolProofAck = () => {
    setShowSchoolProof(false);
    store.updateForm('schoolProofAck', true);
  };

  const onValidate = () => {
    const validated = validateTeacherNewForm(store.form);

    const errors = validated.reduce(
      (accumulator, value) => ({
        ...accumulator,
        [value]: "Must not be empty",
      }),
      {}
    );
    setMessages(errors);

    if (Object.keys(errors).length > 0) {
      setShowAlert(true);
      formRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      setShowAlert(false);
      navigate(`/teacher/app/new/confirmation`);
    }
  };

  const onValidateField = (field) => {
    if (!store.form[field] || store.form[field].trim() === "") {
      setMessages({ ...messages, [field]: "Must not be empty" });
    } else {
      setMessages((current) => {
        const { [field]: key, ...rest } = current;

        return rest;
      });
    }
  };

  return (
    <Layout mType="teacher">
      <div className="bg-gray-100">
        <div className="container mx-auto">
          <div className="lg:py-5 lg:px-0 px-0 py-4">
            <ul className="form-steps">
              <li className="active">Declaration</li>
              <li className="active">Form</li>
              <li>Confirmation</li>
              <li>Completion</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container mx-auto" ref={formRef}>
        <div className="lg:py-8 lg:px-0 px-4 py-4">
          <Breadcrumb mType="teacher" appType="new" />

          <Transition appear show={showSchoolProof} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-10"
              onClose={onSchoolProofAck}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black bg-opacity-25" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        Reminder on required documents
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-gray-500">
                          You are required to upload a copy of{" "}
                          <a
                            href="https://www.lib.eduhk.hk/forms/school-teacher-pe"
                            target="_blank"
                            rel="noreferrer"
                            className="text-orange-600 underline"
                          >
                            proof of employment
                          </a>{" "}
                          when you submit an online application. For details,
                          please refer to{" "}
                          <a
                            href="https://www.lib.eduhk.hk/information-for-school-teacher"
                            target="_blank"
                            rel="noreferrer"
                            className="text-orange-600 underline"
                          >
                            EdUHK Library website
                          </a>
                          .
                        </p>
                      </div>

                      <div className="mt-4">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          onClick={onSchoolProofAck}
                        >
                          Ok, I understand.
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>

          <h3 className="font-bold text-lg mb-8">New Card Application</h3>
          {showAlert && (
            <div
              class="flex p-4 mb-8 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
              role="alert"
            >
              <svg
                aria-hidden="true"
                class="flex-shrink-0 inline w-5 h-5 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="sr-only">Info</span>
              <div>
                <span class="font-medium">Error:</span> Please fix the errors
                below before continuing to the next step.
              </div>
            </div>
          )}
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Card Type</h3>
            </div>
            <div className="px-8 py-8" id="fg-cardType">
              <div class="flex items-center mb-4">
                <input
                  type="radio"
                  id="card-type-reader"
                  name="card_type"
                  value="reader"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("cardType", "reader")}
                  checked={store.form.cardType === "reader"}
                />
                <label for="card-type-reader" class="ml-2">
                  Reader
                </label>
              </div>
              <div class="flex items-center">
                <input
                  type="radio"
                  id="card-type-borrower"
                  name="card_type"
                  value="borrower"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("cardType", "borrower")}
                  checked={store.form.cardType === "borrower"}
                />
                <label for="card-type-borrower" class="ml-2">
                  Borrower
                </label>
              </div>

              <FieldError field={messages.cardType} mt={5} />
            </div>
          </div>
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Card Pickup Location</h3>
            </div>
            <div className="px-8 py-8" id="fg-cardType">
              <div class="flex items-center mb-4">
                <input
                  type="radio"
                  id="pickup-mmw"
                  name="pickup_location"
                  value="mmw"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("pickupLocation", "mmw")}
                  checked={store.form.pickupLocation === "mmw"}
                />
                <label for="pickup-mmw" class="ml-2">
                  MMW Library
                </label>
              </div>
              <div class="flex items-center">
                <input
                  type="radio"
                  id="pickup-tkosc"
                  name="pickup_location"
                  value="tkosc"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("pickupLocation", "tkosc")}
                  checked={store.form.pickupLocation === "tkosc"}
                />
                <label for="pickup-tkosc" class="ml-2">
                  TKOSC Library
                </label>
              </div>

              <FieldError field={messages.pickupLocation} mt={5} />
            </div>
          </div>
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Personal Information</h3>
            </div>
            <div className="lg:px-8 lg:py-8 px-8 py-4">
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Surname:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="surname"
                    placeholder="e.g. Chan"
                    value={store.form.surname}
                    onChange={(e) =>
                      store.updateForm("surname", e.target.value)
                    }
                    onBlur={() => onValidateField("surname")}
                  />
                  <FieldError field={messages.surname} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Other Names:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="other_names"
                    placeholder="e.g. Tai Man"
                    value={store.form.otherNames}
                    onChange={(e) =>
                      store.updateForm("otherNames", e.target.value)
                    }
                    onBlur={() => onValidateField("otherNames")}
                  />
                  <FieldError field={messages.otherNames} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Chinese Name:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="chi_name"
                    value={store.form.chineseName}
                    onChange={(e) =>
                      store.updateForm("chineseName", e.target.value)
                    }
                  />
                  <FieldError field={messages.chineseName} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Passport Photo:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  {!store.form.photo ? (
                    <>
                      <Dropzone
                        width={"lg:w-40 w-full"}
                        height={"lg:h-48 h-48"}
                        format={"image"}
                        minSize={50000}
                        maxSize={3000000}
                        isPassportPhoto={true}
                        maxFiles={1}
                        onAccepted={onPhotoDropAccepted}
                        onRejected={onPhotoDropRejected}
                      />
                      <FieldError field={messages.photo} />
                    </>
                  ) : (
                    <div>
                      <div className="w-40 h-48">
                        <img
                          src={store.form.photo}
                          alt=""
                          className="h-full w-auto object-contain"
                        />
                      </div>
                      <button
                        className="text-red-600 text-sm mt-2 focus:outline-none hover:text-red-700"
                        onClick={onPhotoRemoved}
                      >
                        <svg
                          class="w-4 h-4 inline-block mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Identity Document:
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <div className="relative">
                    <div className="flex">
                      <Menu as="div" className="relative inline-block">
                        <div>
                          <Menu.Button className="flex-shrink-0 z-10 inline-flex items-center py-3 px-4 text-center bg-gray-50 border border-gray-300 rounded-lg mr-2">
                            {store.form.idType === "passport"
                              ? "Passport"
                              : "HKID"}
                            <svg
                              aria-hidden="true"
                              className="ml-1 w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                              ></path>
                            </svg>
                          </Menu.Button>
                        </div>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute left-0 w-32 mt-2 origin-top-left bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="py-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() =>
                                      store.updateForm("idType", "hkid")
                                    }
                                    className={clsx(
                                      active
                                        ? "bg-gray-100 text-gray-900"
                                        : "text-gray-700",
                                      "block text-left w-full px-4 py-2 text-sm"
                                    )}
                                  >
                                    HKID
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() =>
                                      store.updateForm("idType", "passport")
                                    }
                                    className={clsx(
                                      active
                                        ? "bg-gray-100 text-gray-900"
                                        : "text-gray-700",
                                      "block text-left w-full px-4 py-2 text-sm"
                                    )}
                                  >
                                    Passport
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                      <div class="relative w-full">
                        {/* <input
                          type="text"
                          class="block p-3 w-96 z-20 text-gray-900 bg-gray-50 rounded-r-lg border-t border-b border-r border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          value={store.form.idNumber}
                          onChange={(e) =>
                            store.updateForm("idNumber", e.target.value)
                          }
                          onBlur={() => onValidateField("idNumber")}
                          placeholder="Enter the first 4 characters of your HKID/passport. e.g. A123"
                          maxLength="4"
                        /> */}
                        <AutoTabProvider
                          settings={{ pasteToFit: false }}
                          className="flex flex-wrap space-x-2"
                        >
                          {[...Array(8)].map((x, i) => {
                            if (i === 3 || i === 5 || i === 6) {
                              return (
                                <input
                                  key={i}
                                  type="text"
                                  className="text-center bg-gray-50 border border-gray-300 rounded-lg w-14 p-3 mb-1 focus:border-orange-600 focus:ring-orange-600 disabled:bg-gray-200"
                                  onPaste={(e) => e.preventDefault()}
                                  value={store.form.idNumber
                                    ?.substring(i, i + 1)
                                    .trim()}
                                  onChange={(e) => {
                                    const origNumber =
                                      "***" +
                                      (store.form.idNumber?.[3] || " ") +
                                      "*" +
                                      (store.form.idNumber?.[5] || " ") +
                                      (store.form.idNumber?.[6] || " ") +
                                      "*";
                                    const newNumber =
                                      origNumber.substring(0, i) +
                                      (e.target.value || " ") +
                                      origNumber.substring(i + 1);

                                    store.updateForm("idNumber", newNumber);
                                  }}
                                  maxLength={1}
                                  tabbable
                                />
                              );
                            }

                            return (
                              <div
                                key={i}
                                className="text-center bg-gray-200 border border-gray-300 rounded-lg w-14 p-3 mb-1"
                              >
                                *
                              </div>
                            );
                          })}
                        </AutoTabProvider>
                        <div className="flex flex-wrap space-x-2 mb-2">
                          {[...Array(8)].map((x, i) => (
                            <div
                              key={i}
                              className="w-14 text-center text-gray-500 text-xs"
                            >
                              {i + 1}
                              <sup>
                                {i === 0
                                  ? "st"
                                  : i === 1
                                  ? "nd"
                                  : i === 2
                                  ? "rd"
                                  : "th"}
                              </sup>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="mt-1 text-sm">
                    Enter the 4th, 6th and 7th character of your HKID/passport
                    number.
                  </p>
                  <p className="text-sm">
                    For example, enter{" "}
                    <b>
                      <u>3</u>
                    </b>
                    ,{" "}
                    <b>
                      <u>5</u>
                    </b>
                    ,{" "}
                    <b>
                      <u>6</u>
                    </b>{" "}
                    in 4th, 6th, 7th if your HKID is A12
                    <b>
                      <u>3</u>
                    </b>
                    4
                    <b>
                      <u>5</u>
                    </b>
                    <b>
                      <u>6</u>
                    </b>
                    (7).
                  </p>
                  <FieldError field={messages.idNumber} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Home Address:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="home_addr"
                    value={store.form.addrHome}
                    onChange={(e) =>
                      store.updateForm("addrHome", e.target.value)
                    }
                    onBlur={() => onValidateField("addrHome")}
                  />
                  <FieldError field={messages.addrHome} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    <span className="opacity-0">* </span>Contact Address:
                    <Popover className="relative inline-block">
                      <Popover.Button className="focus:outline-none">
                        <svg
                          class="w-6 h-6 inline-block ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                      </Popover.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute left-0 z-10 mt-3 w-screen max-w-sm px-4 sm:px-0 lg:max-w-xl">
                          <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="relative bg-white p-7">
                              Please fill in Contact Address if it is different
                              from Home Address.
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </Popover>
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="contact_addr"
                    value={store.form.addrContact}
                    onChange={(e) =>
                      store.updateForm("addrContact", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Phone Number:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="home_tel"
                    value={store.form.phone}
                    onChange={(e) => store.updateForm("phone", e.target.value)}
                    onBlur={() => onValidateField("phone")}
                  />
                  <FieldError field={messages.phone} />
                </div>
              </div>
              <div className="lg:flex">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* E-mail:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="email"
                    value={store.form.email}
                    onChange={(e) => store.updateForm("email", e.target.value)}
                    onBlur={() => onValidateField("email")}
                  />
                  <p className="text-blue-600 text-sm mt-1">
                    Email notifications such as payment link, confirmation and
                    application status etc will be sent to this email address.
                  </p>
                  <FieldError field={messages.email} />
                </div>
              </div>
            </div>
          </div>

          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Practising School</h3>
            </div>
            <div className="px-8 py-8">
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* School Name:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="school_name"
                    value={store.form.schoolName}
                    onChange={(e) =>
                      store.updateForm("schoolName", e.target.value)
                    }
                    onBlur={() => onValidateField("schoolName")}
                  />
                  <FieldError field={messages.schoolName} />
                </div>
              </div>
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">* Job Position:</label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
                    name="school_position"
                    value={store.form.schoolPosition}
                    onChange={(e) =>
                      store.updateForm("schoolPosition", e.target.value)
                    }
                    onBlur={() => onValidateField("schoolPosition")}
                  />
                  <FieldError field={messages.schoolPosition} />
                </div>
              </div>
            </div>
          </div>

          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Current Employment Status</h3>
            </div>
            <div className="px-8 py-8">
              <div className="lg:flex mb-4">
                <div className="lg:w-72">
                  <label className="inline-block mt-3">
                    * Proof of Employment:
                  </label>
                </div>
                <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                  {store.form.schoolProof ? (
                    <div>
                      <p>
                        <span className="rounded-lg border text-sm px-2 py-1 bg-gray-50">
                          1 file selected
                        </span>
                      </p>
                      <button
                        className="text-red-600 text-sm mt-2 focus:outline-none hover:text-red-700"
                        onClick={onSchoolProofRemoved}
                      >
                        <svg
                          class="w-4 h-4 inline-block mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/gif, application/pdf"
                        maxFiles="1"
                        className="block p-3"
                        name="school_proof"
                        onChange={(e) =>
                          onSchoolProofSelected(e.target.files[0])
                        }
                        onBlur={() => onValidateField("schoolProof")}
                      />
                      <div>
                        <p className="text-gray-500">
                          File format: jpg / gif / png / pdf
                        </p>
                        <p className="text-gray-500">File size: 2MB</p>
                      </div>
                      <FieldError field={messages.schoolProof} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center my-8">
            <button
              className="border rounded-full px-4 py-2 w-48 text-center inline-block text-gray-600 hover:bg-gray-200 transition duration-200 focus:outline-none"
              onClick={() => navigate(`/teacher/app/new`)}
            >
              Back
            </button>
            <button
              className="border border-orange-600 rounded-full px-4 py-2 w-48 text-center inline-block bg-orange-600 text-white ml-8 hover:bg-orange-700 transition duration-200 focus:outline-none"
              onClick={onValidate}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
